import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { teamsApi } from '@/utils/api';
import { toast } from 'sonner';
import { UserPlus, X, ArrowRight } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InviteOnCreateDialog({ team, open, onDone }) {
  const [emails, setEmails] = useState([]);
  const [input, setInput] = useState('');
  const [role, setRole] = useState('editor');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setEmails([]); setInput(''); setRole('editor'); } }, [open, team?.id]);

  const addFromInput = (raw) => {
    const parts = (raw || '').split(/[\s,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return true;
    const bad = parts.find(p => !EMAIL_RE.test(p));
    if (bad) { toast.error(`“${bad}” isn't a valid email`); return false; }
    setEmails(prev => [...new Set([...prev, ...parts])]);
    setInput('');
    return true;
  };

  const close = () => { setEmails([]); setInput(''); setRole('editor'); onDone?.(); };

  const sendInvites = async () => {
    let list = emails;
    if (input.trim()) {
      if (!addFromInput(input)) return;
      list = [...new Set([...emails, ...input.split(/[\s,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean)])];
    }
    if (!list.length) { close(); return; }
    setBusy(true);
    let sent = 0;
    for (const email of list) {
      try { await teamsApi.invite(team.id, email); sent += 1; }
      catch (e) { toast.error(`${email}: ${e?.response?.data?.detail || 'invite failed'}`); }
    }
    setBusy(false);
    if (sent) toast.success(`Invited ${sent} teammate${sent === 1 ? '' : 's'} to ${team.name}`);
    close();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) close(); }}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="invite-on-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus size={17} style={{ color: '#6994c6' }} />Who's in {team?.name}?</DialogTitle>
          <DialogDescription>Invite teammates now so the workspace is ready to use — or skip and invite later.</DialogDescription>
        </DialogHeader>
        {emails.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="invite-email-chips">
            {emails.map(e => (
              <span key={e} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full"
                style={{ background: 'rgba(105,148,198,.12)', color: '#3d5877' }}>
                {e}
                <button onClick={() => setEmails(prev => prev.filter(x => x !== e))} data-testid={`invite-email-remove-${e}`}
                  className="hover:opacity-70"><X size={11} /></button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={input}
            disabled={busy}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addFromInput(input); }
              if (e.key === 'Backspace' && !input && emails.length) setEmails(prev => prev.slice(0, -1));
            }}
            placeholder="teammate@company.com — Enter to add another"
            data-testid="invite-email-input"
            className="flex-1 h-10 px-3 rounded-xl text-sm outline-none border min-w-0"
            style={{ borderColor: 'rgba(33,46,64,.16)' }}
          />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-28 h-10 shrink-0 rounded-xl" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="editor" data-testid="invite-role-editor">Editor</SelectItem>
              <SelectItem value="viewer" data-testid="invite-role-viewer">Viewer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-[11px] text-muted-foreground -mt-1">Editors can capture, edit and assign. Viewers can only browse cards and tasks.</p>
        <div className="flex items-center justify-end gap-2 mt-1">
          <button className="btn-ghost" disabled={busy} onClick={close} data-testid="invite-skip-button">Skip for now</button>
          <button className="btn-green" disabled={busy} onClick={sendInvites} data-testid="invite-send-button">
            {emails.length || input.trim() ? 'Invite & open' : 'Open workspace'} <ArrowRight size={14} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
